import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ExternalLink, Loader2, Plus, Trash2, X } from 'lucide-react';
import IterationForecastPanel from '../../ProjectDetail/IterationForecastPanel';
import IterationTargetsCard from '../../ProjectDetail/IterationTargetsCard';
import { DateField, DateRangeField, InlineArea, InlineText } from '../inline';
import { euros, hours, Label } from '../ui';
import { elapsed, periodState, rangeLabel, toDay } from './dates';
import { sprintProgress, sprintShort } from './colors';
import { DomainNote } from './domains';
import { HoursProjection, ProgressEditor } from './Steering';
import { STATUS, STATUS_ORDER as ORDER } from './objectives';

// Panneau d'une itération : vision, avancement, sprints (avec leurs objectifs), budget, facturation et cibles
// d'attributs ; tout se modifie sur place, sauf ce qu'un domaine à effet réserve à un autre rôle (lecture seule).


const Bar = ({ value, tone = 'bg-indigo-500', className = '' }) => (
  <div className={`h-1.5 overflow-hidden rounded-full bg-slate-100 ${className}`}>
    <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%` }} />
  </div>
);

const Section = ({ title, action, children }) => (
  <div className="border-t border-slate-100 px-4 py-4 sm:px-5">
    <div className="mb-2 flex items-center justify-between gap-2">
      <Label>{title}</Label>
      {action}
    </div>
    {children}
  </div>
);

const Budget = ({ label, used, planned, format }) => {
  const ratio = planned > 0 ? used / planned : 0;
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-sm font-semibold tabular-nums text-slate-800">
        {format(used)} <span className="text-xs font-normal text-slate-400">/ {planned > 0 ? format(planned) : '—'}</span>
      </p>
      {planned > 0 && <Bar value={ratio} tone={ratio > 1 ? 'bg-rose-500' : ratio > 0.85 ? 'bg-amber-400' : 'bg-emerald-500'} className="mt-1.5" />}
    </div>
  );
};

const SprintItem = ({ sprint, index, today, objectives, open, onToggle, onPatch, onDelete, color, openBacklog, readOnly }) => {
  const ref = useRef(null);
  const start = toDay(sprint.start_date);
  const end = toDay(sprint.end_date);
  const state = periodState(start, end, today);
  const done = objectives.filter((o) => o.status === 'done').length;
  useEffect(() => {
    if (open && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [open]);
  return (
    <li ref={ref} className={`rounded-xl border ${open ? 'border-slate-200 bg-white shadow-sm' : 'border-transparent hover:bg-slate-50'}`}>
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-2.5 px-2.5 py-2 text-left">
        <span className={`h-2 w-2 shrink-0 rounded-full ${state === 'current' ? color.dot : state === 'done' ? 'bg-slate-300' : 'bg-slate-200 ring-1 ring-slate-300'}`} />
        <span className="w-9 shrink-0 text-xs font-semibold tabular-nums text-slate-700">{sprintShort(sprint, index)}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-slate-700">{sprint.vision || <span className="text-slate-400">Vision à définir</span>}</span>
          <span className="block text-[11px] text-slate-400">
            {start != null && end != null ? rangeLabel(start, end) : 'Dates à préciser'}
            {objectives.length > 0 && ` · ${done}/${objectives.length} objectifs`}
            {state === 'current' && ' · en cours'}
          </span>
        </span>
        <span className="w-12 shrink-0"><Bar value={sprintProgress(sprint)} tone={color.strong} /></span>
        <ChevronDown size={14} className={`shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-slate-100 px-3 pb-3 pt-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <InlineText value={sprint.description || sprint.name} required ariaLabel="Nom du sprint" readOnly={readOnly}
              onSave={(description) => onPatch({ description })} className="flex-1 text-sm font-medium text-slate-800" />
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <DateField value={sprint.start_date} ariaLabel="Début du sprint" readOnly={readOnly} onSave={(v) => onPatch({ start_date: v })} />
              →
              <DateField value={sprint.end_date} ariaLabel="Fin du sprint" readOnly={readOnly} onSave={(v) => onPatch({ end_date: v })} />
            </span>
          </div>
          <InlineArea value={sprint.vision} readOnly={readOnly} placeholder="Vision du sprint : ce qu'on veut avoir à la fin" onSave={(vision) => onPatch({ vision })} />
          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label>Objectifs</Label>
              <button type="button" onClick={openBacklog} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-indigo-700">
                Backlog <ExternalLink size={11} />
              </button>
            </div>
            {objectives.length ? (
              <ul className="space-y-0.5">
                {objectives.slice().sort((a, b) => (ORDER[a.status] ?? 2) - (ORDER[b.status] ?? 2)).map((objective) => {
                  const status = STATUS[objective.status] || STATUS.not_started;
                  return (
                    <li key={objective.id} className="flex items-baseline gap-2 text-[13px] leading-snug" title={status.label}>
                      <span className={`mt-1 h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full ${status.dot}`} />
                      <span className={`min-w-0 flex-1 ${status.text}`}>{objective.name}</span>
                      {objective.effort ? <span className="shrink-0 text-[11px] tabular-nums text-slate-400">{objective.effort} pt</span> : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-xs text-slate-400">Aucun objectif dans ce sprint.</p>
            )}
          </div>
          {!readOnly && (
            <div className="flex justify-end">
              <button type="button" onClick={onDelete} className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                <Trash2 size={12} /> Supprimer le sprint
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

const BILLING_CHOICES = [
  { value: true, label: 'Facturable', on: 'bg-emerald-600 text-white' },
  { value: false, label: 'Non facturable', on: 'bg-slate-700 text-white' },
  { value: null, label: 'À décider', on: 'bg-white text-slate-800 shadow-sm' },
];

const shortDate = (iso) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '';
};

const Billing = ({ decision, rights, state, onDecide }) => {
  const [saving, setSaving] = useState(false);
  const current = decision?.billable ?? null;
  const decide = async (value) => {
    if (value === current || saving) return;
    setSaving(true);
    try {
      await onDecide(value);
    } catch {
      // erreur affichée dans le bandeau de la fiche
    } finally {
      setSaving(false);
    }
  };
  const toWhom = rights.holders.length ? `à ${rights.holders.map((h) => h.name).join(', ')}` : 'aux leaders du cercle';
  return (
    <div className="space-y-2">
      <div className={`inline-flex rounded-lg bg-slate-100 p-0.5 ${rights.locked ? 'opacity-70' : ''}`}>
        {BILLING_CHOICES.map((choice) => (
          <button key={String(choice.value)} type="button" disabled={rights.locked || saving} onClick={() => decide(choice.value)}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${current === choice.value ? choice.on : 'text-slate-500 hover:text-slate-800'} disabled:cursor-not-allowed`}>
            {choice.label}
          </button>
        ))}
        {saving && <Loader2 size={13} className="m-1.5 animate-spin text-slate-400" />}
      </div>
      <p className="text-[11px] text-slate-500">
        {current === null
          ? state === 'done'
            ? `Pas encore décidé. En fin d'itération, Nexus pose la question ${toWhom}.`
            : `Si rien n'est décidé d'ici là, Nexus posera la question ${toWhom} à la fin de l'itération.`
          : `${current ? 'Facturable' : 'Non facturable'}${decision?.decided_by_name ? `, décidé par ${decision.decided_by_name}` : ''}${decision?.decided_at ? ` le ${shortDate(decision.decided_at)}` : ''}. Les factures prévues suivent.`}
      </p>
      <DomainNote state={rights} />
    </div>
  );
};

const OPEN = { holders: [], can: true, mine: false, locked: false, label: '', who: '' };

const IterationPanel = ({
  iteration, color, today, objectives, attributes, targets, sprintId, projectId, actions, onClose, onSelectSprint, navigate,
  rights = {}, billing, onDecideBillability, progress = null, ca = null, roadmapDomain = null, onSaveProgress = null,
  below = false, // sous la frise (grand écran) : toute la largeur, sections sur deux colonnes, sans défilement propre
}) => {
  const [adding, setAdding] = useState(false);
  const roadmap = rights.roadmap || OPEN;
  const budget = rights.budget || OPEN;
  const readOnly = roadmap.locked; // les sprints, eux, restent à l'équipe
  const traced = roadmap.holders.length > 0; // la fenêtre du motif tient lieu de confirmation
  const start = toDay(iteration.start_date);
  const end = toDay(iteration.end_date);
  const state = periodState(start, end, today);
  const sprintObjectives = useMemo(() => {
    const map = new Map();
    objectives.forEach((o) => { if (o.sprint_id) map.set(o.sprint_id, [...(map.get(o.sprint_id) || []), o]); });
    return map;
  }, [objectives]);
  const iterationTargets = useMemo(() => targets.filter((t) => Number(t.iteration_id) === Number(iteration.id)), [targets, iteration.id]);
  const total = Number(iteration.total_tokens) || 0;
  const completed = Number(iteration.completed_tokens) || 0;
  const daysLeft = end != null ? end - today : null;

  const openBacklog = () => {
    try {
      localStorage.setItem('objectiveBoard_viewMode', 'project');
      localStorage.setItem('objectiveBoard_selectedProject', String(projectId));
    } catch {
      // le backlog s'ouvrira sur sa vue par défaut
    }
    navigate('/objectives');
  };

  const patch = (fields) => actions.patchIteration(iteration.id, fields).catch(() => {}); // erreur : bandeau de la fiche

  const addSprint = async () => {
    setAdding(true);
    try {
      const created = await actions.addSprint(iteration);
      if (created?.id) onSelectSprint(created.id);
    } catch {
      // erreur affichée dans le bandeau de la fiche
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className={below ? '' : 'flex h-full flex-col'}>
      <div className="px-4 pb-3 pt-4 sm:px-5">
        <div className="flex items-start gap-2">
          <span className={`mt-2.5 h-2.5 w-2.5 shrink-0 rounded-full ${color.dot}`} />
          <InlineText value={iteration.name} required ariaLabel="Nom de l'itération" readOnly={readOnly}
            onSave={(name) => patch({ name })}
            className="flex-1 text-lg font-semibold text-slate-900" />
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-4 text-xs text-slate-500">
          <DateRangeField start={iteration.start_date} end={iteration.end_date} readOnly={readOnly} deferred={traced} onSave={patch} />
          {state === 'current' && daysLeft != null && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">encore {daysLeft} j</span>
          )}
          {state === 'upcoming' && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">dans {start - today} j</span>}
          {state === 'done' && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500">terminée</span>}
        </div>
        <div className="mt-2 pl-4">
          <InlineArea value={iteration.description} readOnly={readOnly} placeholder="Vision de l'itération : ce qu'on veut avoir démontré à la fin"
            onSave={(description) => patch({ description })} className={below ? 'max-w-4xl' : ''} />
          <DomainNote state={roadmap} className="mt-1" />
        </div>
      </div>

      <div className={below ? 'grid grid-cols-1 lg:grid-cols-2 lg:[&>*:nth-child(even)]:border-l' : 'min-h-0 flex-1 overflow-y-auto'}>
        <Section title="Avancement">
          <div className="space-y-2.5">
            <ProgressEditor progress={progress} roadmap={roadmapDomain} onSave={onSaveProgress} />
            <div>
              <div className="mb-1 flex justify-between text-xs text-slate-500">
                <span>Temps écoulé</span>
                <span className="tabular-nums">{Math.round(elapsed(start, end, today) * 100)} %</span>
              </div>
              <Bar value={elapsed(start, end, today)} tone="bg-slate-400" />
            </div>
            <div>
              <div className="mb-1 flex justify-between text-xs text-slate-500">
                <span>Objectifs réalisés</span>
                <span className="tabular-nums">{total ? `${completed} / ${total} points` : 'aucun objectif chiffré'}</span>
              </div>
              <Bar value={total ? completed / total : 0} tone={color.strong} />
            </div>
          </div>
        </Section>

        <Section title={`Sprints (${iteration.sprints.length})`} action={(
          <button type="button" onClick={addSprint} disabled={adding}
            className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
            {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Sprint
          </button>
        )}>
          {iteration.sprints.length ? (
            <ul className="-mx-2 space-y-1">
              {iteration.sprints.map((sprint, index) => (
                <SprintItem key={sprint.id} sprint={sprint} index={index} today={today} color={color}
                  objectives={sprintObjectives.get(sprint.id) || []} open={sprintId === sprint.id}
                  onToggle={() => onSelectSprint(sprintId === sprint.id ? null : sprint.id)}
                  onPatch={(fields) => actions.patchSprint(sprint.id, fields).catch(() => {})}
                  onDelete={() => {
                    if (window.confirm(`Supprimer ${sprint.name} ? Ses objectifs resteront dans le backlog.`)) {
                      actions.deleteSprint(sprint.id).catch(() => {});
                      onSelectSprint(null);
                    }
                  }}
                  openBacklog={openBacklog} readOnly={false} />
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">Pas encore de sprint : ajoute le premier (3 semaines, à partir du lundi).</p>
          )}
        </Section>

        <Section title="Budget">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="rounded-xl bg-slate-50 px-3 py-2">
              <p className="text-[11px] text-slate-500">{iteration.is_ca_forecast_overridden ? 'CA réel' : 'CA prévu'}</p>
              <p className="text-sm font-semibold tabular-nums text-slate-800">{euros(iteration.valeur)}</p>
              {ca && <p className="text-[11px] text-slate-400" title="Au % de réalisation de l'itération ; une itération terminée compte en entier">produit {euros(ca.produced)}</p>}
            </div>
            <Budget label="Temps" used={Number(iteration.consumed_time_hours) || 0} planned={Number(iteration.budget_time_hours) || 0} format={hours} />
            <Budget label="Achats" used={Number(iteration.consumed_purchase_eur) || 0} planned={Number(iteration.budget_purchase_eur) || 0} format={euros} />
          </div>
          <div className="mt-2">
            <HoursProjection consumed={Number(iteration.consumed_time_hours) || 0} budget={Number(iteration.budget_time_hours) || 0}
              percent={progress?.percent} />
          </div>
          {budget.locked ? (
            <DomainNote state={budget} className="mt-2" />
          ) : (
            <details className="group mt-2">
              <summary className="flex cursor-pointer list-none items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800">
                <ChevronDown size={12} className="transition group-open:rotate-180" /> Modifier les prévisions
              </summary>
              <div className="mt-2">
                <IterationForecastPanel iteration={iteration}
                  onInlineUpdate={(id, field, value) => actions.patchIteration(id, { [field]: value })} />
              </div>
            </details>
          )}
        </Section>

        {onDecideBillability && (
          <Section title="Facturation">
            <Billing decision={billing} rights={rights.facturabilite || OPEN} state={state}
              onDecide={(value) => onDecideBillability(iteration.id, value)} />
          </Section>
        )}

        <Section title="Cibles d'attributs">
          <IterationTargetsCard iteration={iteration} attributes={attributes} targets={iterationTargets}
            onAddTarget={actions.addTarget} onUpdateTarget={actions.updateTarget} onDeleteTarget={actions.deleteTarget} />
        </Section>

        <div className={`border-t border-slate-100 px-4 py-3 sm:px-5 ${readOnly ? 'hidden' : ''}`}>
          <button type="button" onClick={() => {
            if (traced || window.confirm(`Supprimer l'itération ${iteration.name} et ses ${iteration.sprints.length} sprints ?`)) {
              actions.deleteIteration(iteration.id).then(onClose, () => {});
            }
          }} className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-400 hover:bg-rose-50 hover:text-rose-600">
            <Trash2 size={13} /> Supprimer l'itération
          </button>
        </div>
      </div>
    </div>
  );
};

export default IterationPanel;
